import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

import {
  resolvePlanId,
  PRICING_TIERS,
  GRANDFATHERED_PLAN_DISPLAY,
} from "../../../constants/pricingScreen";
import { shouldHideSaaSPricing } from "../../../utils";

const planName = (planId) => {
  if (!planId) return "your next";
  const id = resolvePlanId(planId);
  if (id === "enterprise") return "Enterprise";
  const tier = PRICING_TIERS.find((t) => t.planId === id);
  if (tier?.name) return tier.name;
  if (GRANDFATHERED_PLAN_DISPLAY[id]?.name)
    return GRANDFATHERED_PLAN_DISPLAY[id].name;
  return id;
};

const formatEffective = (epoch) => {
  if (!epoch) return null;
  return moment.unix(epoch).format("MMM D, YYYY");
};

/**
 * Global banner when a V2 tenant has exceeded volume limits and is scheduled
 * to move to the next plan at their next billing cycle.
 * Reads enterprises.pendingPlanUpgrade via /current_user.
 */
const PendingUpgradeBanner = () => {
  const navigate = useNavigate();
  const pending = useSelector(
    (state) => state?.currentUser?.enterprise?.pendingPlanUpgrade
  );
  const enterprise = useSelector((state) => state?.currentUser?.enterprise);
  const isEnterpriseAdmin = useSelector(
    (state) => state?.currentUser?.user?.isEnterpriseAdmin
  );

  // Network franchise members (Big Frog HQ + locations) don't use SaaS plans.
  if (shouldHideSaaSPricing(enterprise)) return null;
  if (!pending || !isEnterpriseAdmin) return null;
  if (pending.status && pending.status !== "pending") return null;

  const toName = pending.toPlanName || planName(pending.toPlanId);
  const fromName = pending.fromPlanName || planName(pending.fromPlanId);
  const when = formatEffective(pending.effectiveAt);

  if (pending.contactEnterprise) {
    return (
      <Alert
        severity="warning"
        variant="filled"
        sx={{ borderRadius: 0, alignItems: "flex-start" }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>
          You&apos;re over your {fromName} volume limits
        </AlertTitle>
        You&apos;ve exceeded this month&apos;s order or item cap for{" "}
        <b>{fromName}</b>. Contact us about Enterprise for volume-based pricing
        and a dedicated success manager. Your current plan stays active until
        then.
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            color="inherit"
            onClick={() => {
              window.location.href =
                "mailto:sales@riverr.app?subject=Enterprise%20plan%20inquiry";
            }}
            sx={{
              color: "#111",
              bgcolor: "#fff",
              "&:hover": { bgcolor: "#f1f1f1" },
            }}
          >
            Contact us
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => navigate("/pricing")}
            sx={{ color: "#fff", borderColor: "#fff" }}
          >
            View plans
          </Button>
        </Stack>
      </Alert>
    );
  }

  if (!pending.toPlanId) return null;

  return (
    <Alert
      severity="warning"
      variant="filled"
      sx={{ borderRadius: 0, alignItems: "flex-start" }}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        You&apos;re over your {fromName} limits. Moving to {toName}
      </AlertTitle>
      You&apos;ve crossed this month&apos;s order or item threshold for{" "}
      <b>{fromName}</b>. You keep {fromName} for the rest of this billing
      period
      {when ? (
        <>
          {" "}
          (through <b>{when}</b>)
        </>
      ) : null}
      . On your next monthly charge you&apos;ll automatically move to{" "}
      <b>{toName}</b> and be billed at that plan&apos;s rate.
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={() => navigate("/pricing")}
          sx={{
            color: "#111",
            bgcolor: "#fff",
            "&:hover": { bgcolor: "#f1f1f1" },
          }}
        >
          Review plans
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => navigate("/settings/billing")}
          sx={{ color: "#fff", borderColor: "#fff" }}
        >
          Billing details
        </Button>
      </Stack>
    </Alert>
  );
};

export default PendingUpgradeBanner;
